import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Schedule', path: '/schedule' },
  { label: 'Media', path: '/media' },
  { label: 'Gallery', path: '/gallery' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 60);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const isHome = location.pathname === '/';
  const isTransparent = isHome && !scrolled && !mobileOpen;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-700 ease-out will-change-[background-color,backdrop-filter] ${
          isTransparent
            ? 'bg-transparent border-b border-transparent'
            : 'bg-white/95 backdrop-blur-md border-b border-background-200'
        }`}
      >
        <div className="w-full px-6 md:px-10 lg:px-16">
          <div className="flex items-center justify-between h-[72px] md:h-[80px]">
            {/* Brand */}
            <Link
              to="/"
              className="relative z-50 group"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <span className="font-heading text-xl md:text-2xl tracking-[0.15em] uppercase text-foreground-950 transition-colors duration-500">
                Keaton Brown
              </span>
            </Link>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative text-xs tracking-[0.15em] uppercase transition-colors duration-300 whitespace-nowrap ${
                    location.pathname === link.path
                      ? 'text-accent-500'
                      : 'text-foreground-500 hover:text-foreground-950'
                  }`}
                >
                  {link.label}
                  {location.pathname === link.path && (
                    <span className="absolute -bottom-1 left-0 w-full h-[1px] bg-accent-500" />
                  )}
                </Link>
              ))}
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="relative z-50 md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
              aria-label="Toggle menu"
            >
              <div className="flex flex-col gap-[6px]">
                <span
                  className={`block w-6 h-[1.5px] transition-all duration-300 ${
                    mobileOpen ? 'rotate-45 translate-y-[7.5px] bg-foreground-950' : 'bg-foreground-950'
                  }`}
                />
                <span
                  className={`block w-6 h-[1.5px] transition-all duration-300 ${
                    mobileOpen ? 'opacity-0' : 'bg-foreground-950'
                  }`}
                />
                <span
                  className={`block w-6 h-[1.5px] transition-all duration-300 ${
                    mobileOpen ? '-rotate-45 -translate-y-[7.5px] bg-foreground-950' : 'bg-foreground-950'
                  }`}
                />
              </div>
            </button>
          </div>
        </div>
      </nav>

      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 z-[55] md:hidden transition-opacity duration-400 ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileOpen(false)}
      />

      {/* Mobile dropdown panel */}
      <div
        className={`fixed top-0 left-0 w-full z-[56] md:hidden transition-all duration-400 ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div
          className={`bg-white border-b border-background-200 shadow-sm transition-all duration-400 ${
            mobileOpen ? 'translate-y-0' : '-translate-y-4'
          }`}
          style={{
            paddingTop: '72px',
            animation: mobileOpen ? 'mobileMenuSlideDown 0.4s cubic-bezier(0.25, 0.1, 0.25, 1)' : 'none',
          }}
        >
          {/* Close button — top-right */}
          <div className="px-4 pt-2 pb-1 flex justify-end">
            <button
              onClick={() => setMobileOpen(false)}
              className="w-9 h-9 flex items-center justify-center cursor-pointer rounded-full hover:bg-background-100 transition-colors duration-300"
              aria-label="Close menu"
            >
              <i className="ri-close-line text-lg text-foreground-500 hover:text-foreground-950 transition-colors duration-300"></i>
            </button>
          </div>

          <div className="px-4 pb-5 flex flex-col gap-0">
            {navLinks.map((link, i) => (
              <Link
                key={link.path}
                to={link.path}
                className={`py-2.5 px-3 text-sm font-heading tracking-[0.1em] uppercase transition-all duration-300 rounded-lg ${
                  location.pathname === link.path
                    ? 'text-accent-500 bg-accent-50/50'
                    : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100'
                }`}
                style={{
                  opacity: mobileOpen ? 1 : 0,
                  transform: mobileOpen ? 'translateY(0)' : 'translateY(8px)',
                  transition: `all 0.4s cubic-bezier(0.25, 0.1, 0.25, 1) ${i * 50 + 50}ms`,
                }}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}